import { useEffect } from "react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LAST_TOAST_KEY = "ep_moon_toast_last";
const OPT_OUT_KEY   = "ep_moon_toast_opt_out";

/**
 * On page load, checks today's moon phase. If it's a New Moon or a Full
 * Moon and the user hasn't seen the toast today, whispers a warm one-line
 * greeting via sonner. Once per day, dismissable, one-time permanent
 * opt-out via the toast action.
 */
export default function MoonMilestoneToast() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    let optedOut = false;
    try { optedOut = localStorage.getItem(OPT_OUT_KEY) === "1"; } catch {}
    if (optedOut) return;

    const todayKey = new Date().toISOString().slice(0, 10);
    let last = null;
    try { last = localStorage.getItem(LAST_TOAST_KEY); } catch {}
    if (last === todayKey) return;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND}/api/moon/current`, { signal: controller.signal });
        if (!res.ok) return;
        const m = await res.json();
        const phase = String(m?.phase || "").toLowerCase();
        const isNew  = phase === "new moon";
        const isFull = phase === "full moon";
        if (!isNew && !isFull) return;

        const isDawnHour = new Date().getUTCHours() < 12;
        const isFullTonight = isFull && !isDawnHour;
        let title, body;
        if (isNew) {
          title = isDawnHour ? "A new moon at dawn" : "New moon tonight";
          body  = m.effects || "The dark of the moon — a quiet night to plant one small intention.";
        } else {
          title = isFullTonight ? "The moon is full tonight" : "A full moon in the sky";
          body  = m.effects || "The whole tide arrives. Emotions run high — be gentle with yourself.";
        }

        // Mark as shown FIRST so a refresh doesn't re-fire
        try { localStorage.setItem(LAST_TOAST_KEY, todayKey); } catch {}

        toast(title, {
          description: body,
          duration: 14000,
          icon: isFull ? "🌕" : "🌑",
          action: {
            label: "Read guidance",
            onClick: () => { window.location.href = "/almanac"; },
          },
          cancel: {
            label: "Not today",
            onClick: () => {},
          },
        });

        // Second, quiet follow-up so people know they can turn these off
        setTimeout(() => {
          toast("Prefer no moon whispers?", {
            description: "You can turn these off — they'll never appear again.",
            duration: 8000,
            action: {
              label: "Turn off",
              onClick: () => {
                try { localStorage.setItem(OPT_OUT_KEY, "1"); } catch {}
                toast.success("Moon whispers turned off — enjoy the quiet.");
              },
            },
          });
        }, 16000);
      } catch { /* silent — a missed whisper is fine */ }
    }, 4200);

    return () => { clearTimeout(timer); controller.abort(); };
  }, []);

  return null;
}
